import { Maths } from 'helpers/maths';
import {
  PlanWizardFormValues,
  PlanCropZone,
  PlanProductLineItem
} from '../types';
import { initialPlanValues } from '../Constants/planFormikData';

/** Shape returned by `usePlanData(id)` for an existing Plan — only the fields this mapper reads. */
export interface FetchedPlan {
  id?: string;
  organizationId?: string;
  name?: string;
  currency?: string;
  cropId?: string;
  estimatedAreaValue?: number;
  estimatedAreaUnit?: string;
  notes?: string;
  flagIds?: string[];
  fixedCost?: {
    landRent?: number;
    equipment?: number;
    insurance?: number;
    labor?: number;
    repairs?: number;
    taxes?: number;
    returnToMgt?: number;
  };
  projectedRevenue?: {
    yieldPerArea?: number;
    fsaPayment?: number;
    cropSharePercent?: number;
    pricePerUnit?: number;
  };
  cropZones?: Array<{
    /** The crop-zone association's own persisted id (distinct from `cropZoneIdValue`, the referenced field/crop-zone's id). */
    id?: string;
    cropZoneIdValue: string;
    /** The crop season this zone was originally saved against — see `PlanCropZone.cropSeasonId`. */
    cropSeasonId?: string;
    areaValue?: number;
    areaUnit?: string;
    coveragePct?: number;
  }>;
  planProductEntries?: Array<Partial<PlanProductLineItem>>;
}

const calcTotalAreaValue = (cropZones: FetchedPlan['cropZones'] = []): number =>
  (cropZones || []).reduce(
    (sum, z) => Maths.plusRound(sum, z.areaValue || 0),
    0
  );

/** Stub crop zones missing display fields (name/fieldName/crop/propertyName); `PropertiesStep` re-resolves them via `SelectPropertiesDrawerV2`. */
const mapFetchedCropZonesToStubs = (
  cropZones: FetchedPlan['cropZones'] = [],
  { preserveAssociationIds = false }: { preserveAssociationIds?: boolean } = {}
): PlanCropZone[] =>
  (cropZones || []).map(zone => ({
    id: zone.cropZoneIdValue,
    ...(preserveAssociationIds ? { associationId: zone.id } : {}),
    ...(zone.cropSeasonId ? { cropSeasonId: zone.cropSeasonId } : {}),
    area: zone.areaValue ?? 0,
    appliedAcreage: zone.areaValue ?? 0,
    coveragePercent: Maths.multiplyRound(zone.coveragePct ?? 1, 100)
  }));

/** Fetched `planProductEntries` are already denormalized except `coveragePercent`, persisted as a 0-1 fraction vs. the wizard's 0-100 Formik scale. */
const mapFetchedProducts = (
  entries: FetchedPlan['planProductEntries'] = []
): PlanProductLineItem[] =>
  (entries || []).map(entry => ({
    ...(entry as PlanProductLineItem),
    coveragePercent: Maths.multiplyRound(entry.coveragePercent ?? 1, 100)
  }));

/** Reverse of `usePlanWizardMutations`'s `buildPlanCreatePayload`; `blankName` matches legacy `Plan/index.jsx`'s Duplicate handling (blank name only). */
export const mapFetchedPlanToWizardValues = (
  plan: FetchedPlan,
  {
    blankName = false,
    preserveAssociationIds = false
  }: { blankName?: boolean; preserveAssociationIds?: boolean } = {}
): PlanWizardFormValues => {
  const estimatedAreaValue = plan.estimatedAreaValue || 1;
  const cropZones = mapFetchedCropZonesToStubs(plan.cropZones, {
    preserveAssociationIds
  });
  const totalAppliedAcreage = calcTotalAreaValue(plan.cropZones);

  return {
    name: blankName ? '' : plan.name || '',
    cropId: plan.cropId || '',
    estimatedAreaValue,
    estimatedAreaUnit: plan.estimatedAreaUnit || 'acre',
    area: estimatedAreaValue,
    // FR-088: a field the API never returned (`undefined`) maps to `null`
    // here — not `0` — so it renders blank instead of a fake zero. Rounded
    // to 2dp to match PlanDetailPanel.helpers.tsx's `perUnitTotal`.
    fixedCost: {
      landRent:
        plan.fixedCost?.landRent == null
          ? null
          : Maths.multiplyRound(plan.fixedCost.landRent, estimatedAreaValue, 2),
      landRent_PerAc: plan.fixedCost?.landRent ?? null,
      equipment:
        plan.fixedCost?.equipment == null
          ? null
          : Maths.multiplyRound(
              plan.fixedCost.equipment,
              estimatedAreaValue,
              2
            ),
      equipment_PerAc: plan.fixedCost?.equipment ?? null,
      insurance:
        plan.fixedCost?.insurance == null
          ? null
          : Maths.multiplyRound(
              plan.fixedCost.insurance,
              estimatedAreaValue,
              2
            ),
      insurance_PerAc: plan.fixedCost?.insurance ?? null,
      labor:
        plan.fixedCost?.labor == null
          ? null
          : Maths.multiplyRound(plan.fixedCost.labor, estimatedAreaValue, 2),
      labor_PerAc: plan.fixedCost?.labor ?? null,
      repairs:
        plan.fixedCost?.repairs == null
          ? null
          : Maths.multiplyRound(plan.fixedCost.repairs, estimatedAreaValue, 2),
      repairs_PerAc: plan.fixedCost?.repairs ?? null,
      taxes:
        plan.fixedCost?.taxes == null
          ? null
          : Maths.multiplyRound(plan.fixedCost.taxes, estimatedAreaValue, 2),
      taxes_PerAc: plan.fixedCost?.taxes ?? null,
      returnToMgt:
        plan.fixedCost?.returnToMgt == null
          ? null
          : Maths.multiplyRound(
              plan.fixedCost.returnToMgt,
              estimatedAreaValue,
              2
            ),
      returnToMgt_PerAc: plan.fixedCost?.returnToMgt ?? null
    },
    projectedRevenue: {
      yieldPerArea: plan.projectedRevenue?.yieldPerArea ?? null,
      yieldTotal: Maths.multiplyRound(
        Maths.multiplyRound(
          plan.projectedRevenue?.yieldPerArea || 0,
          estimatedAreaValue
        ),
        plan.projectedRevenue?.pricePerUnit || 0
      ),
      pricePerUnit: plan.projectedRevenue?.pricePerUnit ?? null,
      fsaPayment: plan.projectedRevenue?.fsaPayment ?? null,
      fsaPaymentPerAc:
        plan.projectedRevenue?.fsaPayment == null
          ? null
          : Maths.divideSafeRound(
              plan.projectedRevenue.fsaPayment,
              estimatedAreaValue
            ),
      // `?? 1` (not `|| 0`, unlike the other fields above): a genuinely
      // persisted 0% crop share must survive the reverse mapping — `|| 0`
      // would make `0 * 100 || 100` collapse it back to the 100% default,
      // silently changing a real 0% Plan to 100% on every edit-mode load.
      cropSharePercent: Maths.multiplyRound(
        plan.projectedRevenue?.cropSharePercent ?? 1,
        100
      )
    },
    notes: plan.notes || '',
    tagIds: plan.flagIds || [],
    cropZones,
    totalAppliedAcreage,
    planProductEntries: mapFetchedProducts(plan.planProductEntries),
    isEditOrDuplicateMode: true
  };
};

/** Create mode has no source Plan to fetch — always the same blank starting point. */
export const getCreateInitialValues = (): PlanWizardFormValues =>
  initialPlanValues;
